import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  Select,
  Stack,
  Text,
  Tooltip,
  VStack,
} from "components/ui";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import {
  MdAdd,
  MdCheckCircle,
  MdContentCopy,
  MdEdit,
  MdExpandLess,
  MdExpandMore,
  MdToggleOff,
  MdToggleOn,
  MdWarning,
} from "react-icons/md";
import { ToolConfig, toolService } from "services/toolService";
import DeleteConfirmPopover from "components/common/DeleteConfirmPopover";
import { ListSkeleton } from "./ListSkeleton";
import ToolCreator from "./ToolCreator";
import ToolEditor from "./ToolEditor";

interface ToolsListProps {
  serverId: string;
  refreshKey?: number;
}

const SKIP_PATH_PREFIXES = new Set([
  "api",
  "apis",
  "openapi",
  "rest",
  "service",
  "services",
  "public",
  "private",
  "internal",
  "v1",
  "v2",
  "v3",
  "v4",
  "v5",
  "1.0",
  "2.0",
  "3.0",
]);

// KEEP IN SYNC with ToolGroupResolver.ResolveGroupName in
// api-agents.api/Domain/ApiAgents.Domain.Services/Helpers/ToolGroupResolver.cs.
// The backend groups tools by the same derivation to build the planner's capability
// groups — if the two diverge, enabling "the Users group" here yields a different set
// than the planner can select at runtime.
const getControllerName = (endpointPath: string): string => {
  const parts = endpointPath.replace(/^\/+/, "").split("/").filter(Boolean);
  const first = parts.find(
    (p) => !SKIP_PATH_PREFIXES.has(p.toLowerCase()) && !p.startsWith("{"),
  );
  if (!first) return "Other";
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

export const ToolsList: React.FC<ToolsListProps> = ({
  serverId,
  refreshKey,
}) => {
  const { t } = useTranslation();

  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [riskLevelFilter, setRiskLevelFilter] = useState<
    "all" | "low" | "medium" | "high"
  >("all");

  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [isCreatingTool, setIsCreatingTool] = useState(false);
  const [togglingToolIds, setTogglingToolIds] = useState<Set<string>>(
    new Set(),
  );
  const [deletingToolIds, setDeletingToolIds] = useState<Set<string>>(
    new Set(),
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [isBulkEnabling, setIsBulkEnabling] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [bulkOperatingGroups, setBulkOperatingGroups] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    loadTools();
  }, [serverId, refreshKey]);

  const loadTools = async () => {
    try {
      setLoading(true);
      const result = await toolService.getServerTools(serverId);
      setTools(result);
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.errors.failedToLoadTools"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      if (statusFilter === "active" && !tool.isActive) return false;
      if (statusFilter === "inactive" && tool.isActive) return false;
      if (riskLevelFilter !== "all" && tool.riskLevel !== riskLevelFilter)
        return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          tool.name.toLowerCase().includes(q) ||
          tool.displayName.toLowerCase().includes(q) ||
          tool.description.toLowerCase().includes(q) ||
          tool.endpointPath.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [tools, statusFilter, riskLevelFilter, searchQuery]);

  const groupedTools = useMemo(() => {
    const map = new Map<string, ToolConfig[]>();
    for (const tool of filteredTools) {
      const group = getControllerName(tool.endpointPath);
      const existing = map.get(group) ?? [];
      map.set(group, [...existing, tool]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
  }, [filteredTools]);

  const toggleGroupExpanded = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const handleGroupEnable = async (
    groupName: string,
    groupTools: ToolConfig[],
  ) => {
    const ids = groupTools.filter((t) => !t.isActive).map((t) => t.id);
    if (ids.length === 0) return;
    setBulkOperatingGroups((prev) => new Set(prev).add(groupName));
    try {
      await toolService.bulkEnableByIds(serverId, ids);
      setTools((prev) =>
        prev.map((t) => (ids.includes(t.id) ? { ...t, isActive: true } : t)),
      );
      toaster.create({
        title: t("app.common.success"),
        description: t("app.servers.tools.groups.enableSuccess", {
          count: ids.length,
        }),
        type: "success",
      });
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.tools.groups.enableError"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setBulkOperatingGroups((prev) => {
        const s = new Set(prev);
        s.delete(groupName);
        return s;
      });
    }
  };

  const handleGroupDisable = async (
    groupName: string,
    groupTools: ToolConfig[],
  ) => {
    const ids = groupTools.filter((t) => t.isActive).map((t) => t.id);
    if (ids.length === 0) return;
    setBulkOperatingGroups((prev) => new Set(prev).add(groupName));
    try {
      await toolService.bulkDisableByIds(serverId, ids);
      setTools((prev) =>
        prev.map((t) => (ids.includes(t.id) ? { ...t, isActive: false } : t)),
      );
      toaster.create({
        title: t("app.common.success"),
        description: t("app.servers.tools.groups.disableSuccess", {
          count: ids.length,
        }),
        type: "success",
      });
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.tools.groups.disableError"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setBulkOperatingGroups((prev) => {
        const s = new Set(prev);
        s.delete(groupName);
        return s;
      });
    }
  };

  const handleToggleTool = async (tool: ToolConfig) => {
    try {
      setTogglingToolIds((prev) => new Set(prev).add(tool.id));
      const updatedTool = await toolService.toggleToolStatus(
        serverId,
        tool.id,
        !tool.isActive,
      );
      setTools((prev) =>
        prev.map((t) => (t.id === updatedTool.id ? updatedTool : t)),
      );
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.errors.failedToToggleTool"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setTogglingToolIds((prev) => {
        const next = new Set(prev);
        next.delete(tool.id);
        return next;
      });
    }
  };

  const handleDeleteTool = async (tool: ToolConfig) => {
    try {
      setDeletingToolIds((prev) => new Set(prev).add(tool.id));
      await toolService.deleteTool(serverId, tool.id);
      setTools((prev) => prev.filter((t) => t.id !== tool.id));
      toaster.create({
        title: t("app.servers.tools.delete.success"),
        type: "success",
      });
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.tools.delete.failed"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setDeletingToolIds((prev) => {
        const next = new Set(prev);
        next.delete(tool.id);
        return next;
      });
    }
  };

  const handleToolSaved = (updatedTool: ToolConfig) => {
    setTools((prev) =>
      prev.map((t) => (t.id === updatedTool.id ? updatedTool : t)),
    );
    setEditingToolId(null);
  };

  const handleToolCreated = () => {
    loadTools();
    setIsCreatingTool(false);
  };

  const handleBulkEnable = async (riskLevel?: "low" | "medium" | "high") => {
    try {
      setIsBulkEnabling(true);
      const result = await toolService.bulkEnableByRisk(serverId, riskLevel);
      await loadTools();
      toaster.create({
        title: t("app.common.success"),
        description: riskLevel
          ? t("app.servers.tools.bulkEnable.successByRisk", {
              count: result.enabledCount,
              riskLevel: t(`app.servers.tools.riskLevels.${riskLevel}`),
            })
          : t("app.servers.tools.bulkEnable.successAll", {
              count: result.enabledCount,
            }),
        type: "success",
      });
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.tools.bulkEnable.error"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setIsBulkEnabling(false);
    }
  };

  const renderToolCard = (tool: ToolConfig) => {
    if (editingToolId === tool.id) {
      return (
        <ToolEditor
          key={tool.id}
          tool={tool}
          serverId={serverId}
          onSave={handleToolSaved}
          onCancel={() => setEditingToolId(null)}
        />
      );
    }

    return (
      <Card.Root key={tool.id} overflow="hidden" w="full" maxW="full">
        <Card.Body p={4}>
          <Flex justify="space-between" align="start" w="full" maxW="full">
            <Box flex={1} minW={0} overflow="hidden">
              <HStack mb={2} gap={2} flexWrap="wrap">
                <Heading
                  size="sm"
                  color={{ base: "navy.700", _dark: "white" }}
                  wordBreak="break-word"
                >
                  {tool.displayName || tool.name}
                </Heading>
                {tool.displayName && (
                  <Text
                    fontSize="xs"
                    color={{ base: "gray.500", _dark: "gray.500" }}
                  >
                    ({tool.name})
                  </Text>
                )}
                <Badge
                  bg={{ base: "blue.100", _dark: "blue.900" }}
                  color={{ base: "blue.700", _dark: "blue.300" }}
                  px={2}
                  py={0.5}
                  fontSize="xs"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  {tool.httpMethod}
                </Badge>
                <Tooltip
                  content={t(
                    `app.servers.tools.riskTooltips.${tool.riskLevel}`,
                  )}
                >
                  <Badge
                    colorPalette={
                      tool.riskLevel === "high"
                        ? "red"
                        : tool.riskLevel === "medium"
                          ? "yellow"
                          : "green"
                    }
                    px={2}
                    py={0.5}
                    fontSize="xs"
                    fontWeight="medium"
                    flexShrink={0}
                  >
                    {t(`app.servers.tools.riskLevels.${tool.riskLevel}`)}
                  </Badge>
                </Tooltip>
                {tool.isDestructive && (
                  <Tooltip content={t("app.servers.tools.destructiveTooltip")}>
                    <Badge
                      colorPalette="red"
                      px={2}
                      py={0.5}
                      fontSize="xs"
                      fontWeight="medium"
                      flexShrink={0}
                    >
                      <MdWarning size={12} style={{ marginRight: "4px" }} />
                      {t("app.servers.tools.destructive")}
                    </Badge>
                  </Tooltip>
                )}
                {tool.isBulk && (
                  <Tooltip content={t("app.servers.tools.bulkTooltip")}>
                    <Badge
                      colorPalette="purple"
                      px={2}
                      py={0.5}
                      fontSize="xs"
                      fontWeight="medium"
                      flexShrink={0}
                    >
                      <MdContentCopy size={12} style={{ marginRight: "4px" }} />
                      {t("app.servers.tools.bulk")}
                    </Badge>
                  </Tooltip>
                )}
                <Badge
                  bg={
                    tool.isActive
                      ? { base: "green.100", _dark: "green.900" }
                      : { base: "gray.100", _dark: "gray.700" }
                  }
                  color={
                    tool.isActive
                      ? { base: "green.700", _dark: "green.300" }
                      : { base: "gray.700", _dark: "gray.300" }
                  }
                  px={2}
                  py={0.5}
                  fontSize="xs"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  {tool.isActive ? (
                    <>
                      <MdCheckCircle size={12} style={{ marginRight: "4px" }} />
                      {t("app.common.active")}
                    </>
                  ) : (
                    t("app.common.inactive")
                  )}
                </Badge>
              </HStack>
              <Text
                mb={2}
                fontSize="sm"
                color={{ base: "gray.600", _dark: "gray.400" }}
                wordBreak="break-word"
              >
                {tool.description}
              </Text>
              <Text
                fontSize="xs"
                color={{ base: "gray.500", _dark: "gray.500" }}
                wordBreak="break-all"
                overflowWrap="anywhere"
              >
                {tool.endpointPath}
              </Text>
            </Box>
            <VStack gap={2} ml={3} flexShrink={0}>
              <Button
                onClick={() => handleToggleTool(tool)}
                disabled={
                  togglingToolIds.has(tool.id) || editingToolId !== null
                }
                variant="outline"
                colorPalette={tool.isActive ? "red" : "green"}
                size="sm"
                w="full"
              >
                {tool.isActive ? (
                  <>
                    <MdToggleOff />
                    {t("app.actions.disable")}
                  </>
                ) : (
                  <>
                    <MdToggleOn />
                    {t("app.actions.enable")}
                  </>
                )}
              </Button>
              <Button
                onClick={() => setEditingToolId(tool.id)}
                disabled={editingToolId !== null}
                colorPalette="blue"
                size="sm"
                w="full"
              >
                <MdEdit />
                {t("app.actions.edit")}
              </Button>
              <DeleteConfirmPopover
                onConfirm={() => handleDeleteTool(tool)}
                loading={deletingToolIds.has(tool.id)}
                disabled={editingToolId !== null}
                fullWidth
                title={t("app.servers.tools.delete.confirmTitle")}
                message={t("app.servers.tools.delete.confirmIrreversible")}
                triggerLabel={t("app.servers.tools.delete.button")}
                confirmLabel={t("app.common.delete")}
              />
            </VStack>
          </Flex>
        </Card.Body>
      </Card.Root>
    );
  };

  const renderGroup = (groupName: string, groupTools: ToolConfig[]) => {
    const isGroupExpanded = expandedGroups.has(groupName);
    const activeCount = groupTools.filter((t) => t.isActive).length;
    const isOperating = bulkOperatingGroups.has(groupName);
    const allActive = activeCount === groupTools.length;
    const noneActive = activeCount === 0;

    return (
      <Card.Root key={groupName} w="full" maxW="full" overflow="hidden">
        <Card.Body p={0}>
          {/* Group header */}
          <HStack
            px={4}
            py={3}
            justify="space-between"
            borderBottomWidth={isGroupExpanded ? "1px" : "0"}
            borderColor={{ base: "gray.200", _dark: "gray.700" }}
            cursor="pointer"
            onClick={() => toggleGroupExpanded(groupName)}
            _hover={{ bg: { base: "gray.50", _dark: "gray.700" } }}
            transition="background 0.15s"
          >
            <HStack gap={3}>
              {isGroupExpanded ? (
                <MdExpandLess size={18} />
              ) : (
                <MdExpandMore size={18} />
              )}
              <Heading size="sm" color={{ base: "navy.700", _dark: "white" }}>
                {groupName}
              </Heading>
              <Badge
                colorPalette={activeCount > 0 ? "green" : "gray"}
                px={2}
                py={0.5}
                fontSize="xs"
              >
                {t("app.servers.tools.groups.activeCount", {
                  active: activeCount,
                  total: groupTools.length,
                })}
              </Badge>
            </HStack>
            <HStack gap={2} onClick={(e) => e.stopPropagation()}>
              <Button
                size="xs"
                variant="outline"
                colorPalette="green"
                disabled={isOperating || allActive || editingToolId !== null}
                onClick={() => handleGroupEnable(groupName, groupTools)}
              >
                {t("app.servers.tools.groups.enableGroup")}
              </Button>
              <Button
                size="xs"
                variant="outline"
                colorPalette="red"
                disabled={isOperating || noneActive || editingToolId !== null}
                onClick={() => handleGroupDisable(groupName, groupTools)}
              >
                {t("app.servers.tools.groups.disableGroup")}
              </Button>
            </HStack>
          </HStack>

          {/* Group tools */}
          {isGroupExpanded && (
            <Stack gap={2} p={3}>
              {groupTools.map((tool) => renderToolCard(tool))}
            </Stack>
          )}
        </Card.Body>
      </Card.Root>
    );
  };

  const bodyContent = () => {
    if (isCreatingTool) {
      return (
        <ToolCreator
          serverId={serverId}
          onSave={handleToolCreated}
          onCancel={() => setIsCreatingTool(false)}
        />
      );
    }

    if (loading) {
      return <ListSkeleton count={5} showMiddleColumn={true} />;
    }

    if (tools.length === 0) {
      return (
        <Card.Root w="full" maxW="full">
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.600", _dark: "white" }}
            >
              {t("app.servers.tools.noToolsFound")}
            </Text>
          </Card.Body>
        </Card.Root>
      );
    }

    if (filteredTools.length === 0) {
      return (
        <Card.Root w="full" maxW="full">
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.600", _dark: "white" }}
            >
              {t("app.servers.tools.noToolsMatchSearch")}
            </Text>
          </Card.Body>
        </Card.Root>
      );
    }

    return (
      <Stack gap={3} w="full" maxW="full">
        {groupedTools.map(([groupName, groupToolsList]) =>
          renderGroup(groupName, groupToolsList),
        )}
      </Stack>
    );
  };

  return (
    <Box w="full" maxW="full">
      {/* Section header */}
      <HStack mb={4} justify="space-between">
        <HStack gap={2}>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="ghost"
            size="sm"
            p={2}
          >
            {isExpanded ? (
              <MdExpandLess size={24} />
            ) : (
              <MdExpandMore size={24} />
            )}
          </Button>
          <Heading size="lg" color={{ base: "navy.700", _dark: "white" }}>
            {t("app.servers.tools.title", { count: tools.length })}
          </Heading>
        </HStack>
      </HStack>

      {isExpanded && (
        <>
          {/* Toolbar */}
          <HStack
            mb={4}
            gap={3}
            justify="flex-end"
            flexWrap="wrap"
            align="flex-start"
          >
            <Box flexShrink={0}>
              <Select
                items={[
                  { label: t("app.filters.allStatus"), value: "all" },
                  { label: t("app.common.active"), value: "active" },
                  { label: t("app.common.inactive"), value: "inactive" },
                ]}
                value={[statusFilter]}
                onValueChange={({ value }) =>
                  setStatusFilter(value[0] as typeof statusFilter)
                }
                w="140px"
                size="sm"
              />
            </Box>
            <Box flexShrink={0}>
              <Select
                items={[
                  { label: t("app.servers.tools.allRiskLevels"), value: "all" },
                  {
                    label: t("app.servers.tools.riskLevels.low"),
                    value: "low",
                  },
                  {
                    label: t("app.servers.tools.riskLevels.medium"),
                    value: "medium",
                  },
                  {
                    label: t("app.servers.tools.riskLevels.high"),
                    value: "high",
                  },
                ]}
                value={[riskLevelFilter]}
                onValueChange={({ value }) =>
                  setRiskLevelFilter(value[0] as typeof riskLevelFilter)
                }
                w="160px"
                size="sm"
              />
            </Box>
            <HStack gap={2} flexShrink={0}>
              <Input
                type="text"
                placeholder={t("app.servers.tools.searchPlaceholder")}
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setSearchQuery(pendingSearch);
                }}
                w={{ base: "full", sm: "256px" }}
                h="40px"
              />
              <Button
                onClick={() => setSearchQuery(pendingSearch)}
                colorPalette="blue"
                variant="outline"
              >
                {t("app.common.search")}
              </Button>
            </HStack>
            <MenuRoot>
              <MenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={isBulkEnabling || editingToolId !== null}
                >
                  {t("app.servers.tools.bulkEnable.label")}
                  <MdExpandMore />
                </Button>
              </MenuTrigger>
              <MenuContent>
                <MenuItem value="enable-all" onClick={() => handleBulkEnable()}>
                  {t("app.servers.tools.bulkEnable.enableAll")}
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  value="enable-low"
                  onClick={() => handleBulkEnable("low")}
                >
                  {t("app.servers.tools.bulkEnable.enableLow")}
                </MenuItem>
                <MenuItem
                  value="enable-medium"
                  onClick={() => handleBulkEnable("medium")}
                >
                  {t("app.servers.tools.bulkEnable.enableMedium")}
                </MenuItem>
                <MenuItem
                  value="enable-high"
                  onClick={() => handleBulkEnable("high")}
                >
                  {t("app.servers.tools.bulkEnable.enableHigh")}
                </MenuItem>
              </MenuContent>
            </MenuRoot>
            <Button
              onClick={() => setIsCreatingTool(true)}
              disabled={editingToolId !== null}
              colorPalette="blue"
              variant="solid"
            >
              <MdAdd />
              {t("app.servers.tools.addTool")}
            </Button>
          </HStack>

          {bodyContent()}
        </>
      )}
    </Box>
  );
};

export default ToolsList;
